// HTTP 402 LAWPAY_DECLINED keeps data.error as the message (the full decline sentence). It does not open the subscription-lapsed notice. Delivered 2026-11-01.
// One-time eCheck charge sends method "bank" and authorization_text. Saving a bank account, and turning autopay on for one, send the same sentence. Delivered 2026-11-01.
// pkg198 — clientPayMethods / clientPaySaveMethod / clientPayDeleteMethod / clientPayMethodsConfig / clientPayAutopay / clientPaySetAutopay / clientPayPlans / clientPayPlan (saved LawPay methods, autopay, plan view; token + consent only) — delivered 2026-11-01
// pkg177 — clientInvoice / clientInvoiceDownload (client-portal invoice detail modal + .docx download) — delivered 2026-10-04
// pkg172a — clientPaySummary / clientPayConfig / clientPayCharge (client-portal billing + LawPay in-portal card pay) — delivered 2026-10-03
// pkg144 — stepparent adoption intake client methods (getAdoptionIntake / saveAdoptionIntake / submitAdoptionIntake) — delivered 2026-08-11
// pkg71 — checkSignupCode: live validation for the signup wizard's optional referral/invitation code field (D-71b/h) — delivered 2026-07-16
// pkg65 — 402 SUBSCRIPTION_REQUIRED dispatches the client paused-portal event (firm's subscription lapsed, D-65d) — delivered 2026-07-11
// pkg57 — shared-documents client methods (getMySharedDocuments / downloadSharedDocument) — delivered 2026-07-09
// pkg41 — acknowledgment/e-sign client methods (status / open / document blob / sign / download acknowledged copy) — delivered 2026-07-04
// pkg36 — saveFirmModuleDraft (wizard save-as-you-go) — delivered 2026-07-03
// pkg26 — client methods for the Asset & Liability Ledger module (getAssetLedger / taxonomy / add+update+delete asset|liability / submit)
// pkg22 — F12: removed the dead `adminApi` export (called admin endpoints with the CLIENT token; zero importers) — delivered 2026-06-12
// pkg21 — client methods for firm-private AI modules (getMyFirmModules / getMyFirmModule / submitFirmModule) — delivered 2026-06-11
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function getToken() { return localStorage.getItem('ryanlaw_token'); }

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch (_) { /* non-JSON body */ }
  if (!res.ok) {
    // pkg65 — the FIRM's subscription lapsed: tell Layout to show the
    // paused-portal notice (clients can't fix billing; the owner can).
    if (res.status === 402 && data.code === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new CustomEvent('lm:subscription-required'));
    }
    // Preserve structured error info (code, current row on 409) so the
    // discovery workspace can render a conflict modal.
    // LAWPAY_DECLINED puts the full sentence in error. Keep it on both
    // message and serverError so the pay screens can show it unchanged.
    const serverError = typeof data.error === 'string' ? data.error : '';
    const err = new Error(serverError || 'Request failed');
    err.status  = res.status;
    err.code    = data.code;
    err.serverError = serverError;
    err.current = data.current;
    err.consent_text = data.consent_text;
    throw err;
  }
  return data;
}

async function download(path) {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    if (res.status === 402) { // pkg65 — firm subscription lapsed
      window.dispatchEvent(new CustomEvent('lm:subscription-required'));
    }
    throw new Error('Download failed');
  }
  return res.blob();
}

export const api = {
  // Auth
  login: (email, password, rememberMe = false) =>
    request('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password, rememberMe: !!rememberMe }) }),
  changePassword: (currentPassword, newPassword) =>
    request('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  me: () => request('/api/auth/me'),

  // Demographic
  getDemographic:    () => request('/api/demographic'),
  saveDemographic:   (data) => request('/api/demographic/save',   { method: 'POST', body: JSON.stringify({ data }) }),
  submitDemographic: (data) => request('/api/demographic/submit', { method: 'POST', body: JSON.stringify({ data }) }),

  // Parenting plan
  getParentingPlan:    () => request('/api/parenting-plan'),
  saveParentingPlan:   (data) => request('/api/parenting-plan/save', { method: 'POST', body: JSON.stringify({ data }) }),
  sendParentingPlan:   (data, recipientEmail) =>
    request('/api/parenting-plan/send', { method: 'POST', body: JSON.stringify({ data, recipientEmail }) }),

  // Witnesses
  getWitnesses:    () => request('/api/witnesses'),
  saveWitnesses:   (data) => request('/api/witnesses/save',   { method: 'POST', body: JSON.stringify({ data }) }),
  submitWitnesses: (data) => request('/api/witnesses/submit', { method: 'POST', body: JSON.stringify({ data }) }),

  // Budget
  getBudget:      () => request('/api/budget'),
  saveBudget:     (data) => request('/api/budget/save',   { method: 'POST', body: JSON.stringify({ data }) }),
  submitBudget:   (data) => request('/api/budget/submit', { method: 'POST', body: JSON.stringify({ data }) }),
  downloadBudgetTemplate: () => download('/api/budget/download'),

  // Links
  getLinks: () => request('/api/links'),

  // Dashboard status
  getStatus: () => request('/api/status'),

  // Important dates (client read-only)
  getDates:       () => request('/api/dates'),
  getGlobalDates: () => request('/api/global-dates'),

  // Calendar feed (subscribe-to-iCal). The client can enable/rotate/disable
  // their own feed token. While active, the returned `feedUrl` (https) and
  // `webcalUrl` (webcal:// — one-tap subscribe on iOS, macOS, Android) point
  // at the public ICS endpoint and stay valid until rotated or revoked.
  getCalendarSync:    () => request('/api/calendar/me'),
  enableCalendarSync: () => request('/api/calendar/me', { method: 'POST' }),
  disableCalendarSync:() => request('/api/calendar/me', { method: 'DELETE' }),

  // Per-event .ics download. Returns a Blob the caller can hand to a hidden
  // <a download> link to trigger a one-time import into the client's
  // calendar app. Works universally — including on Samsung Calendar /
  // Android, where there's no system-wide subscribe protocol. `isGlobal`
  // picks between firm-wide dates (true) and the client's own dates (false).
  // NOT a subscription — future reschedules won't propagate to the user's
  // calendar through this path. For live updates, use the subscribe feed
  // above (getCalendarSync / enableCalendarSync).
  downloadEventIcs: (isGlobal, dateId) =>
    download(`/api/calendar/event/${isGlobal ? 'global' : 'client'}/${dateId}`),

  // Trust (Phase 3) — client sees own balance + recent activity
  getMyTrust:     () => request('/api/trust/me'),

  // Juvenile Court Info Form (Custody + Dependency & Neglect)
  getJuvenileInfo:      () => request('/api/juvenile-info'),
  saveJuvenileInfo:     (data) => request('/api/juvenile-info/save',   { method: 'POST', body: JSON.stringify({ data }) }),
  submitJuvenileInfo:   (data) => request('/api/juvenile-info/submit', { method: 'POST', body: JSON.stringify({ data }) }),
  downloadJuvenileInfo: () => download('/api/juvenile-info/download'),

  // ── Discovery Response (Phase 2 — client portal) ──────────────────────
  // Returns ONLY responses the attorney has marked visible_to_client or
  // locked. Drafts are invisible to the client by design.
  listClientDiscovery:   () => request('/api/client-discovery-response'),
  getClientDiscovery:    (id) => request(`/api/client-discovery-response/${id}`),
  // saveClientDiscoveryAnswer always sends version for optimistic locking.
  // A 409 surfaces as err.status === 409 with err.current = the row that
  // beat us; callers render the conflict modal off that.
  saveClientDiscoveryAnswer: (id, qid, payload) =>
    request(`/api/client-discovery-response/${id}/questions/${qid}/answer`,
      { method: 'PUT', body: JSON.stringify(payload) }),
  getClientDiscoveryPresence:  (id) =>
    request(`/api/client-discovery-response/${id}/presence`),
  sendClientDiscoveryPresence: (id, questionId) =>
    request(`/api/client-discovery-response/${id}/presence`,
      { method: 'POST', body: JSON.stringify({ question_id: questionId }) }),

  // ── Modules system (Phase 3 — client portal) ──────────────────────────
  // GET /api/modules/me returns the modules the signed-in client has been
  // assigned, filtered to active ones, sorted by sort_order. html_content
  // for content_html modules is inlined in the response so the dashboard
  // and sidebar don't need a separate fetch per module.
  getMyModules: () => request('/api/modules/me'),

  // PDFs are streamed by ID. The iframe used for display can't carry an
  // Authorization header, so we download the bytes here and the caller
  // turns the blob into an object URL for <iframe src>.
  getMyModulePdfBlob: (id) => download(`/api/modules/me/${id}/pdf`),

  // ── Firm-private AI modules (pkg21) ────────────────────────────────────
  // Active firm modules assigned to the signed-in client. The list endpoint
  // includes schema_json plus submission_id/submitted_at; the single-module
  // endpoint additionally returns prior answers for pre-fill. Submit upserts
  // (one row per client per module), so re-submitting updates answers.
  getMyFirmModules: ()   => request('/api/firm-modules/me'),
  // pkg57 — documents the firm shared to this client's portal
  getMySharedDocuments:   ()   => request('/api/firm-modules/me/shared-documents'),
  downloadSharedDocument: (id) => download(`/api/firm-modules/me/shared-documents/${id}/download`),
  getMyFirmModule:  (id) => request(`/api/firm-modules/me/${id}`),
  submitFirmModule: (id, answers) =>
    request(`/api/firm-modules/me/${id}/submit`, { method: 'POST', body: JSON.stringify({ answers }) }),
  saveFirmModuleDraft: (id, answers) => // pkg36 — wizard auto-save between steps
    request(`/api/firm-modules/me/${id}/save`, { method: 'POST', body: JSON.stringify({ answers }) }),

  // ── Acknowledgment / e-sign (pkg41) ─────────────────────────────────────
  // After submitting an assembly module that requires acknowledgment, the
  // client opens a frozen snapshot (rendered once, stored server-side),
  // views it in-portal (docx-preview renders the document blob), and signs
  // with a typed name + attestation checkbox. Download of the acknowledged
  // copy is available only after signing.
  getMyAcknowledgment: (id) => request(`/api/firm-modules/me/${id}/acknowledgment`),
  openMyAcknowledgment: (id) =>
    request(`/api/firm-modules/me/${id}/acknowledgment/open`, { method: 'POST' }),
  getMyAcknowledgmentDocBlob: (id, ackId) =>
    download(`/api/firm-modules/me/${id}/acknowledgment/${ackId}/document`),
  signMyAcknowledgment: (id, ackId, typedName) =>
    request(`/api/firm-modules/me/${id}/acknowledgment/${ackId}/sign`,
      { method: 'POST', body: JSON.stringify({ typed_name: typedName }) }),
  downloadMyAcknowledgedCopy: (id, ackId) =>
    download(`/api/firm-modules/me/${id}/acknowledgment/${ackId}/download`),

  // ── Asset & Liability Ledger (pkg26) ───────────────────────────────────
  // Standalone client module. The page drives its categories/sub-types from
  // getAssetLedgerTaxonomy(); item bodies are sent as flat field objects
  // (snake_case) matching the pkg25 routes — not wrapped in { data }.
  getAssetLedger:         () => request('/api/asset-ledger'),
  getAssetLedgerTaxonomy: () => request('/api/asset-ledger/taxonomy'),
  addAsset:        (payload)     => request('/api/asset-ledger/assets', { method: 'POST', body: JSON.stringify(payload) }),
  updateAsset:     (id, payload) => request(`/api/asset-ledger/assets/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteAsset:     (id)          => request(`/api/asset-ledger/assets/${id}`, { method: 'DELETE' }),
  addLiability:    (payload)     => request('/api/asset-ledger/liabilities', { method: 'POST', body: JSON.stringify(payload) }),
  updateLiability: (id, payload) => request(`/api/asset-ledger/liabilities/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteLiability: (id)          => request(`/api/asset-ledger/liabilities/${id}`, { method: 'DELETE' }),
  submitAssetLedger: ()          => request('/api/asset-ledger/submit', { method: 'POST' }),
  downloadAssetLedger: ()        => download('/api/asset-ledger/download'), // pkg31

  // ── Stepparent Adoption Intake (pkg144) ────────────────────────────────
  getAdoptionIntake:    () => request('/api/adoption-intake'),
  saveAdoptionIntake:   (data) => request('/api/adoption-intake/save',   { method: 'POST', body: JSON.stringify({ data }) }),
  submitAdoptionIntake: (data) => request('/api/adoption-intake/submit', { method: 'POST', body: JSON.stringify({ data }) }),

  // ── Invoices & Payments / LawPay (pkg172a) ─────────────────────────────
  clientPaySummary: ()     => request('/api/client-pay/summary'),
  clientPayConfig:  ()     => request('/api/client-pay/config'),
  clientPayCharge:  (body) => request('/api/client-pay/charge', { method: 'POST', body: JSON.stringify(body) }),
  // pkg198 — saved methods, autopay, and the client's plan view. POST bodies
  // carry a LawPay hosted-fields token (or a saved method id), never a PAN.
  clientPayMethodsConfig: ()     => request('/api/client-pay/methods/config'),
  clientPayMethods:       ()     => request('/api/client-pay/methods'),
  clientPaySaveMethod:    (body) => request('/api/client-pay/methods', { method: 'POST', body: JSON.stringify(body) }),
  clientPayDeleteMethod:  (id)   => request(`/api/client-pay/methods/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  clientPayAutopay:       ()     => request('/api/client-pay/autopay'),
  clientPaySetAutopay:    (body) => request('/api/client-pay/autopay', { method: 'POST', body: JSON.stringify(body) }),
  clientPayPlans:         ()     => request('/api/client-pay/plans'),
  clientPayPlan:          (id)   => request(`/api/client-pay/plans/${encodeURIComponent(id)}`),
  clientInvoice:         (id) => request(`/api/client-pay/invoices/${id}`),          // pkg177
  clientInvoiceDownload: (id) => download(`/api/client-pay/invoices/${id}/download`), // pkg177
};

// Public (unauthenticated) endpoints — used on login screens and the
// first-run setup wizard, where there's no token or admin key available yet.
async function publicRequest(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const text2 = await res.text();
  let data = {};
  try { data = JSON.parse(text2); } catch (_) { /* non-JSON body */ }
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
}

export const publicApi = {
  getFirmInfo: () => publicRequest('/api/public/firm-info'),
  getSetupStatus: () => publicRequest('/api/setup/status'),
  initializeSetup: (payload) => publicRequest('/api/setup/initialize', {
    method: 'POST', body: JSON.stringify(payload),
  }),

  // ── Signup (pkg15v2) ──────────────────────────────────────────────
  checkSubdomain: (subdomain) =>
    publicRequest('/api/signup/check-subdomain', { method: 'POST', body: JSON.stringify({ subdomain }) }),
  registerFirm: (payload) =>
    publicRequest('/api/signup/register', { method: 'POST', body: JSON.stringify(payload) }),
  verifyFirm: (token) =>
    publicRequest('/api/signup/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  resendVerification: (email, subdomain) =>
    publicRequest('/api/signup/resend-verification', { method: 'POST', body: JSON.stringify({ email, subdomain }) }),
  checkSignupCode: (code) => // pkg71 (D-71b/h) — { kind: 'invite'|'referral'|'unknown'|'none', valid?, referrer_name? }
    publicRequest('/api/signup/check-code', { method: 'POST', body: JSON.stringify({ code }) }),

  // ── Invite acceptance (pkg15v2) ───────────────────────────────────
  validateInvite: (code) => publicRequest(`/api/invites/accept/${encodeURIComponent(code)}`),
  acceptInvite: (code, payload) =>
    publicRequest(`/api/invites/accept/${encodeURIComponent(code)}`, { method: 'POST', body: JSON.stringify(payload) }),

  // ── Universal login (pkg15v2) ─────────────────────────────────────
  universalLogin: (email, password) =>
    publicRequest('/api/universal-login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  universalSelect: (pickerToken, firmId) =>
    publicRequest('/api/universal-login/select', { method: 'POST', body: JSON.stringify({ pickerToken, firmId }) }),
  universalHandoff: (handoffToken) =>
    publicRequest('/api/universal-login/handoff', { method: 'POST', body: JSON.stringify({ handoffToken }) }),

  // Staff setup and reset emails land on /reset-password?token=…, which posts
  // the token and the new password. Unauthenticated.
  completePasswordSetup: (token, password) =>
    publicRequest('/api/auth/complete-password-setup', { method: 'POST', body: JSON.stringify({ token, password }) }),
};
